import { PromisePool, addParameterToURL } from "bm-ui-kit";
import type CancelablePromise from "cancelable-promise";
import type { ReactNode } from "react";
import { Container } from "react-bootstrap";
import { AbstractMenu } from "./AbstractMenu";
import { MenuItem } from "./components/MenuItem";
import { CORPORATE_MENU, RETAIL_MENU } from "./data";
import type { Menu, MenuCategory, MenuItemCategory, Product } from "./type";

export class DesktopMenu extends AbstractMenu {
	private readonly promisePool = new PromisePool();
	private timeoutPromise: CancelablePromise;

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	public render(): ReactNode {
		const { variant } = this.state;
		const menuItems = variant === "retail" ? RETAIL_MENU : CORPORATE_MENU;

		return (
			<Container>
				<div className="desktop-menu" onMouseLeave={() => this.changeOpenedMenu()}>
					<ul className="desktop-main-menu pt-1 mb-0">
						{menuItems.map((item, key) => this.renderMainMenuItem(item, key, this.renderSubMenu(item)))}
						<li className={`type-change ${variant === "retail" ? "active" : ""} py-1 px-3 ml-auto`}>
							<a href="/?link_type=top-menu-item">Lakossági</a>
						</li>
						<li className={`type-change ${variant === "corporate" ? "active" : ""} py-1 px-3 ml-1`}>
							<a href="/vallalati/?link_type=corporate-top-menu-item">Vállalati</a>
						</li>
					</ul>
				</div>
			</Container>
		);
	}

	private renderMainMenuItem(item: MenuItemCategory, key: number, children: ReactNode): ReactNode {
		const { openedMenu } = this.state;
		const { title } = item;

		return (
			<li key={key} className={`py-1${openedMenu && openedMenu.title === title ? " hover" : ""}`}>
				<a
					href="#"
					onMouseOver={() => this.changeOpenedMenu(item)}
					onFocus={() => this.changeOpenedMenu(item)}
					className="px-3"
				>
					{title}
				</a>
				{children}
			</li>
		);
	}

	private renderSubMenu(currentItem: Menu): ReactNode {
		const { openedMenu, openedSubMenu } = this.state;
		const subMenuItems = openedSubMenu?.items ?? [];
		const subMenuItemsLength = subMenuItems.length;

		if (!currentItem || this.isHrMenuItem(currentItem)) {
			return null;
		}

		return (
			<div
				className={`${openedMenu && openedMenu.title === currentItem.title ? "d-flex" : "d-none"} align-items-top`}
			>
				{this.isCategoryMenuItem(currentItem) && (
					<ul className="box my-2">
						{currentItem.items.map((item, ikey) => (
							<li key={`submenu_li_${ikey}`}>
								{this.renderLevelItem(item, ikey, "primary")}
								{openedSubMenu && subMenuItemsLength > 0 && (
									<ul className="sub-menu">
										{subMenuItems.map((subItem, sikey) => this.renderLevelItem(subItem, sikey))}
									</ul>
								)}
							</li>
						))}
					</ul>
				)}
				<div className="box my-2">
					{this.renderCenterBox(
						currentItem.infoBoxContent,
						currentItem.productInfo,
						openedSubMenu,
						subMenuItems,
					)}
				</div>
				<div className="w-100 my-2 px-3">
					{this.renderInfoBox(
						currentItem.infoBoxContent,
						currentItem.productInfo,
						openedSubMenu,
						subMenuItemsLength,
					)}
				</div>
			</div>
		);
	}

	private renderCenterBox(
		infoBoxContent: ReactNode,
		productInfo: Product,
		openedSubMenu: MenuItemCategory,
		subMenuItems: Menu[],
	): ReactNode {
		const subMenuItemsLength = subMenuItems.length;

		return (
			<>
				{!openedSubMenu && productInfo && this.renderProduct(productInfo)}
				{openedSubMenu && subMenuItemsLength > 0 && (
					<div className="box invisible">
						{subMenuItems.map((item, key) => this.renderLevelItem(item, key))}
					</div>
				)}
				{openedSubMenu &&
					infoBoxContent &&
					subMenuItemsLength === 0 &&
					this.renderProduct(openedSubMenu.productInfo, productInfo)}
			</>
		);
	}

	private renderInfoBox(
		infoBoxContent: ReactNode,
		productInfo: Product,
		openedSubMenu: MenuItemCategory,
		subMenuItemsLength: number,
	): ReactNode {
		const { openedSubMenuLevel2 } = this.state;

		if (openedSubMenuLevel2?.productInfo) {
			return this.renderProduct(openedSubMenuLevel2.productInfo);
		}

		if (openedSubMenu) {
			if (subMenuItemsLength === 0) {
				if (infoBoxContent) {
					return <div className="mt-2">{infoBoxContent}</div>;
				}

				return this.renderProduct(openedSubMenu.productInfo, productInfo);
			}

			if (openedSubMenu.productInfo) {
				return this.renderProduct(openedSubMenu.productInfo);
			}
		}

		if (infoBoxContent) {
			return <div className="mt-2">{infoBoxContent}</div>;
		}

		if (productInfo) {
			return this.renderProduct(productInfo);
		}

		return null;
	}

	private renderProduct(product: Product, parentProduct?: Product): ReactNode {
		const finalProduct = product || parentProduct;

		if (!finalProduct) {
			return null;
		}

		const { title, text } = finalProduct;

		return (
			<div className="p-2">
				<span className="product-desc-title pb-2">{title}</span>
				<p className="product-desc-text">{text}</p>
			</div>
		);
	}

	protected renderLevelItem(item: Menu, key: number, menuCategory: MenuCategory = "sub-menu"): ReactNode {
		const { variant } = this.props;
		const isPrimaryCategory = menuCategory === "primary";

		if (this.isHrMenuItem(item)) {
			return <div key={`level2_hr_${key}`} className="hr my-2 mx-2" />;
		}

		const extendedUrl = this.isCategoryMenuItem(item)
			? undefined
			: addParameterToURL(item.url, {
					link_type: `${variant === "retail" ? "top-menu-item" : "corporate-top-menu-item"}-${menuCategory}`,
				});

		const hasSubItems = this.isCategoryMenuItem(item);

		return (
			<MenuItem
				hasSubItems={hasSubItems}
				title={item.title}
				productCount={item.productCount}
				prepend={item.iconElement}
				append={hasSubItems ? undefined : item.badge}
				key={`submenu_${key}`}
				href={extendedUrl}
				onMouseOver={() => this.onSelectSubmenu(this.isCategoryMenuItem(item) ? item : undefined, menuCategory)}
				onFocus={() => this.onSelectSubmenu(this.isCategoryMenuItem(item) ? item : undefined, menuCategory)}
				onMouseMove={
					isPrimaryCategory
						? () => this.changeOpenedSubMenu(this.isCategoryMenuItem(item) ? item : undefined)
						: undefined
				}
				onMouseLeave={isPrimaryCategory ? () => this.clearTimeout() : undefined}
			/>
		);
	}

	protected changeOpenedMenu(menu: MenuItemCategory = undefined): void {
		this.clearTimeout();
		super.changeOpenedMenu(menu);
	}

	protected changeOpenedSubMenu(menu: MenuItemCategory): void {
		const { openedSubMenu } = this.state;

		if (openedSubMenu) {
			// first open
			this.setState({
				openedSubMenu: menu,
			});
		} else if (!menu || openedSubMenu?.title !== menu?.title) {
			this.clearTimeout();

			this.timeoutPromise = this.promisePool.addTimeout(80).then(() => {
				super.changeOpenedSubMenu(menu);
				this.timeoutPromise = undefined;
			});
		}
	}

	protected changeOpenedSubMenuLevel2(menu: MenuItemCategory): void {
		this.clearTimeout();
		super.changeOpenedSubMenuLevel2(menu);
	}

	private clearTimeout(): void {
		if (this.timeoutPromise) {
			this.timeoutPromise.cancel();
			this.timeoutPromise = undefined;
		}
	}
}
